import React from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { strategyMeta, strategySections } from '../data/strategy';
import type { StrategyBlock } from '../data/strategy';
interface StrategyOverlayProps {
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
function Block({
  block
}: {
  block: StrategyBlock;
}) {
  switch (block.kind) {
    case 'accent':
      return <p className="my-4 border-l-2 border-blue pl-4 font-display text-[17px] font-semibold leading-snug text-blue" data-id="element-470" data-mp="e7t1rs78bhfj">
          {block.text}
        </p>;
    case 'sub':
      return <h4 className="mt-6 font-display text-[14px] font-semibold uppercase tracking-tight text-navy" data-id="element-471" data-mp="4bhpv3bjwtlm">
          {block.text}
        </h4>;
    case 'list':
      return <ul className="my-3 flex flex-col gap-1.5" data-id="element-472" data-mp="1yoz5m45psm63">
          {block.items.map(item => <li key={item} className="flex gap-2.5 text-[14px] leading-relaxed text-mut" data-id="element-473" data-mp="23nk521d571th">
              <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-blue" aria-hidden="true" data-id="element-474" data-mp="8gaemupl2ctk" />
              {item}
            </li>)}
        </ul>;
    default:
      return <p className="mt-3 text-[14px] leading-relaxed text-mut" data-id="element-475" data-mp="3fcysos1puv8">{block.text}</p>;
  }
}
export function StrategyOverlay({
  onClose
}: StrategyOverlayProps) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-navy/40 px-4 py-6 sm:px-6" role="dialog" aria-modal="true" aria-label={strategyMeta.title} data-id="element-476" data-mp="d8adskxtx1p3">
      <motion.div initial={{
      opacity: 0,
      y: 12,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="h-fit w-full max-w-2xl overflow-hidden rounded-2xl bg-paper shadow-soft" data-id="element-477" data-mp="3podyc6we6tid">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-line bg-paper px-6 py-5 sm:px-8" data-id="element-478" data-mp="2kqwqc4bxh9r">
          <div data-id="element-479" data-mp="7isix5x1xwpg">
            <p className="text-[9px] uppercase tracking-micro text-blue" data-id="element-480" data-mp="7iabdd4xvw28">Internal · strategy</p>
            <h2 className="mt-2 font-display text-[19px] font-semibold leading-snug text-navy" data-id="element-481" data-mp="blykw1d4t1q4">
              {strategyMeta.title}
            </h2>
            <p className="mt-1 text-[12.5px] text-mut" data-id="element-482" data-mp="3fzn7rhh0lgkj">{strategyMeta.subtitle}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close strategy document" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line text-mut transition-colors duration-150 hover:border-mut hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-483" data-mp="3k18kn2tjtuuq">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-484" data-mp="2w9lahwkiayf5" />
          </button>
        </header>

        <div className="px-6 py-7 sm:px-8" data-id="element-485" data-mp="318o5eo6rvo9e">
          <p className="rounded-xl bg-mist px-4 py-3 text-[12px] leading-relaxed text-mut" data-id="element-486" data-mp="2trrjfnmsms4s">
            {strategyMeta.note}
          </p>

          {strategySections.map(section => <section key={section.number} className="mt-9 border-t border-line pt-6 first:border-0" data-id="element-487" data-mp="2jsseog55fgn2">
              <p className="font-display text-[11px] font-bold tabular-nums tracking-micro text-blue" data-id="element-488" data-mp="2fs01xcx2itm1">
                {section.number}
              </p>
              <h3 className="mt-1.5 font-display text-[21px] font-semibold leading-snug text-navy" data-id="element-489" data-mp="2atosh3aoql4c">
                {section.title}
              </h3>
              {section.blocks.map((block, i) => <Block key={i} block={block} data-id="element-490" data-mp="210hpfp3f3h7f" data-mpi={i} />)}
            </section>)}

          <section className="mt-10 rounded-2xl border border-line bg-mist px-6 py-7" data-id="element-491" data-mp="1w5aogp1j8qm7">
            <p className="text-[9px] uppercase tracking-micro text-blue" data-id="element-492" data-mp="1s1ctmqj8q1yy">Core strategic idea</p>
            <p className="mt-3 font-display text-[24px] font-semibold leading-[1.1] text-navy" data-id="element-493" data-mp="1n3b7txufjojs">
              {strategyMeta.coreIdea}
            </p>
            <p className="mt-4 text-[13.5px] leading-relaxed text-mut" data-id="element-494" data-mp="3r6e6wi6jnbx7">
              {strategyMeta.coreSupport}
            </p>
            <p className="mt-5 border-l-2 border-blue pl-4 font-display text-[16px] leading-snug text-blue" data-id="element-495" data-mp="3de6fz71xyf6f">
              {strategyMeta.finalLine}
            </p>
          </section>

          <button type="button" onClick={onClose} className="mt-8 flex h-12 w-full items-center justify-center rounded-full border border-line text-[13px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-496" data-mp="33gsa21dn98sd">
            Close strategy
          </button>
        </div>
      </motion.div>
    </motion.div>;
}